import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { useAuth } from "../../hooks/useAuth";
import "./admin.css";

// ==================== PAGE CONFIGURATIONS ====================
const EDITABLE_PAGES = {
  home: {
    id: "home",
    name: "Home (Landing Page)",
    icon: "🏠",
    firebaseDoc: "page_configs/home",
    sections: [
      { id: "hero", name: "Hero Section", type: "hero", hasVideo: true, hasText: true },
      { id: "trailer", name: "Event Trailer", type: "video", hasVideo: true, hasText: true },
      { id: "leaderboard", name: "Leaderboard", type: "data", hasText: true },
      { id: "metrics", name: "Quick Metrics", type: "stats", hasText: true },
    ]
  },
  onboardingGame: {
    id: "onboardingGame",
    name: "Onboarding Game",
    icon: "🎮",
    firebaseDoc: "page_configs/onboarding_game",
    sections: [
      { id: "road", name: "Interactive Road", type: "custom", hasText: true },
      { id: "modules", name: "Video Modules (m0-m11)", type: "modules", hasVideo: true, hasText: true },
      { id: "sections", name: "Section Headers", type: "text", hasText: true },
    ]
  },
  insightCenter: {
    id: "insightCenter",
    name: "Insight Center",
    icon: "💡",
    firebaseDoc: "page_configs/insight_center",
    sections: [
      { id: "intro", name: "Main Intro Video", type: "video", hasVideo: true, hasText: true },
      { id: "easterEgg", name: "Easter Egg Video", type: "video", hasVideo: true, hasText: true },
      { id: "pageLinks", name: "Page Links", type: "links", hasText: true },
    ]
  },
  trainingCenter: {
    id: "trainingCenter",
    name: "Training Center",
    icon: "🎓",
    firebaseDoc: "page_configs/training_center",
    sections: [
      { id: "hero", name: "Hero Section", type: "hero", hasText: true },
      { id: "dashboard", name: "Progress Dashboard", type: "stats", hasText: true },
      { id: "levels", name: "Level Progress", type: "progress", hasText: true },
    ]
  },
  business: {
    id: "business",
    name: "Business",
    icon: "📊",
    firebaseDoc: "config/business_videos",
    sections: [
      { id: "hero", name: "Business Intro Video", type: "video", hasVideo: true, hasText: true },
      { id: "sections", name: "Video Sections", type: "gallery", hasVideo: true, hasText: true },
      { id: "faq", name: "FAQ Section", type: "faq", hasVideo: true, hasText: true },
    ]
  },
  education: {
    id: "education",
    name: "Education",
    icon: "📚",
    firebaseDoc: "config/education_videos",
    sections: [
      { id: "hero", name: "Education Intro Video", type: "video", hasVideo: true, hasText: true },
      { id: "sections", name: "Video Sections", type: "gallery", hasVideo: true, hasText: true },
      { id: "faq", name: "FAQ Sections", type: "faq", hasVideo: true, hasText: true },
    ]
  },
  fileVault: {
    id: "fileVault",
    name: "File Vault",
    icon: "🗂️",
    firebaseDoc: "config/file_vault",
    sections: [
      { id: "sections", name: "Vault Sections (12 categories)", type: "vault", hasText: true, hasLinks: true },
      { id: "items", name: "Section Items", type: "vault-items", hasText: true, hasLinks: true },
    ]
  },
  systematicTools: {
    id: "systematicTools",
    name: "10 Systematic Tools",
    icon: "🛠️",
    firebaseDoc: "page_configs/systematic_tools",
    sections: [
      { id: "header", name: "Page Header", type: "header", hasText: true },
      { id: "tools", name: "Tool Cards", type: "cards", hasText: true, hasLinks: true },
    ]
  },
  trackMyLicense: {
    id: "trackMyLicense",
    name: "Track My License",
    icon: "🪪",
    firebaseDoc: "config/track_my_license_videos",
    sections: [
      { id: "chapters", name: "Chapter Videos (14 chapters)", type: "chapters", hasVideo: true, hasText: true },
      { id: "progress", name: "Progress Tracking", type: "progress", hasText: true },
    ]
  },
  crashCourse: {
    id: "crashCourse",
    name: "Crash Course",
    icon: "⚡",
    firebaseDoc: "config/crash_course_videos",
    sections: [
      { id: "header", name: "Course Header", type: "header", hasText: true },
      { id: "videos", name: "Course Videos", type: "video-list", hasVideo: true, hasText: true },
    ]
  },
  licensingDocuments: {
    id: "licensingDocuments",
    name: "Licensing Documents",
    icon: "📄",
    firebaseDoc: "config/licensing_documents",
    sections: [
      { id: "header", name: "Page Header", type: "header", hasText: true },
      { id: "documents", name: "Document Cards", type: "documents", hasText: true, hasLinks: true },
    ]
  },
};

export default function PageBuilder() {
  const { isAdmin } = useAuth();
  const [selectedPage, setSelectedPage] = useState("home");
  const [selectedSection, setSelectedSection] = useState(null);
  const [pageData, setPageData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [viewMode, setViewMode] = useState("desktop"); // desktop, tablet, mobile
  const [editMode, setEditMode] = useState("visual"); // visual, code

  const currentPage = EDITABLE_PAGES[selectedPage];

  useEffect(() => {
    loadPageData(selectedPage);
  }, [selectedPage]);

  const loadPageData = async (pageId) => {
    setLoading(true);
    try {
      const page = EDITABLE_PAGES[pageId];
      const docRef = doc(db, page.firebaseDoc);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setPageData(prev => ({
          ...prev,
          [pageId]: docSnap.data()
        }));
      } else {
        // Initialize empty structure
        setPageData(prev => ({
          ...prev,
          [pageId]: initializePageData(page)
        }));
      }
    } catch (error) {
      console.error("Error loading page data:", error);
      showMessage("Error loading page data: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const initializePageData = (page) => {
    const data = {
      pageTitle: page.name,
      sections: {}
    };
    
    page.sections.forEach(section => {
      data.sections[section.id] = {
        visible: true,
        title: section.name,
        order: Object.keys(data.sections).length,
        content: {}
      };
    });
    
    return data;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const page = EDITABLE_PAGES[selectedPage];
      const docRef = doc(db, page.firebaseDoc);
      await setDoc(docRef, pageData[selectedPage] || {}, { merge: true });
      showMessage(`✅ ${page.name} saved successfully!`, "success");
    } catch (error) {
      console.error("Error saving:", error);
      showMessage("❌ Error saving: " + error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const updateSectionField = (sectionId, field, value) => {
    setPageData(prev => ({
      ...prev,
      [selectedPage]: {
        ...prev[selectedPage],
        sections: {
          ...prev[selectedPage]?.sections,
          [sectionId]: {
            ...prev[selectedPage]?.sections?.[sectionId],
            [field]: value
          }
        }
      }
    }));
  };

  const updateSectionContent = (sectionId, contentKey, value) => {
    setPageData(prev => ({
      ...prev,
      [selectedPage]: {
        ...prev[selectedPage],
        sections: {
          ...prev[selectedPage]?.sections,
          [sectionId]: {
            ...prev[selectedPage]?.sections?.[sectionId],
            content: {
              ...prev[selectedPage]?.sections?.[sectionId]?.content,
              [contentKey]: value
            }
          }
        }
      }
    }));
  };

  const toggleSectionVisibility = (sectionId) => {
    const section = pageData[selectedPage]?.sections?.[sectionId];
    updateSectionField(sectionId, "visible", !section?.visible);
  };

  const moveSectionUp = (sectionId) => {
    const sections = pageData[selectedPage]?.sections || {};
    const currentOrder = sections[sectionId]?.order || 0;
    
    if (currentOrder > 0) {
      // Find section with previous order
      const prevSection = Object.entries(sections).find(
        ([_, s]) => s.order === currentOrder - 1
      );
      
      if (prevSection) {
        updateSectionField(sectionId, "order", currentOrder - 1);
        updateSectionField(prevSection[0], "order", currentOrder);
      }
    }
  };

  const moveSectionDown = (sectionId) => {
    const sections = pageData[selectedPage]?.sections || {};
    const currentOrder = sections[sectionId]?.order || 0;
    const maxOrder = Object.keys(sections).length - 1;
    
    if (currentOrder < maxOrder) {
      // Find section with next order
      const nextSection = Object.entries(sections).find(
        ([_, s]) => s.order === currentOrder + 1
      );
      
      if (nextSection) {
        updateSectionField(sectionId, "order", currentOrder + 1);
        updateSectionField(nextSection[0], "order", currentOrder);
      }
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 3000);
  };

  const getSortedSections = () => {
    const sections = pageData[selectedPage]?.sections || {};
    return Object.entries(sections).sort((a, b) => (a[1].order || 0) - (b[1].order || 0));
  };

  if (!isAdmin) {
    return (
      <div className="admin-page">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>Only administrators can access the Page Builder.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading page editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-builder-modern">
      {/* Header */}
      <div className="pb-header">
        <div className="pb-header-left">
          <h1 className="pb-title">
            <span className="pb-icon">🎨</span>
            Visual Page Builder
          </h1>
          <p className="pb-subtitle">Edit and customize all user-facing pages</p>
        </div>
        <div className="pb-header-right">
          <div className="view-mode-selector">
            <button
              className={`view-btn ${viewMode === "desktop" ? "active" : ""}`}
              onClick={() => setViewMode("desktop")}
              title="Desktop View"
            >
              🖥️
            </button>
            <button
              className={`view-btn ${viewMode === "tablet" ? "active" : ""}`}
              onClick={() => setViewMode("tablet")}
              title="Tablet View"
            >
              📱
            </button>
            <button
              className={`view-btn ${viewMode === "mobile" ? "active" : ""}`}
              onClick={() => setViewMode("mobile")}
              title="Mobile View"
            >
              📱
            </button>
          </div>
          <button
            className="save-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-small"></span>
                Saving...
              </>
            ) : (
              <>💾 Save Changes</>
            )}
          </button>
        </div>
      </div>

      {/* Message Banner */}
      {message.text && (
        <div className={`vm-message vm-message-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Page Selector */}
      <div className="pb-page-selector">
        {Object.values(EDITABLE_PAGES).map(page => (
          <button
            key={page.id}
            className={`pb-page-btn ${selectedPage === page.id ? "active" : ""}`}
            onClick={() => setSelectedPage(page.id)}
          >
            <span className="page-icon">{page.icon}</span>
            <span className="page-name">{page.name}</span>
          </button>
        ))}
      </div>

      {/* Main Editor Area */}
      <div className="pb-main-area">
        {/* Left Sidebar - Section List */}
        <div className="pb-sidebar-left">
          <div className="sidebar-header">
            <h3>📋 Page Sections</h3>
            <p className="sidebar-desc">Drag to reorder, click to edit</p>
          </div>

          <div className="section-list">
            {getSortedSections().map(([sectionId, sectionData]) => {
              const sectionConfig = currentPage.sections.find(s => s.id === sectionId);
              if (!sectionConfig) return null;

              return (
                <div
                  key={sectionId}
                  className={`section-item ${selectedSection === sectionId ? "selected" : ""} ${!sectionData.visible ? "hidden" : ""}`}
                  onClick={() => setSelectedSection(sectionId)}
                >
                  <div className="section-item-header">
                    <div className="section-item-left">
                      <div className="drag-handle">⋮⋮</div>
                      <span className="section-item-name">{sectionData.title || sectionConfig.name}</span>
                    </div>
                    <div className="section-item-actions">
                      <button
                        className="section-action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSectionUp(sectionId);
                        }}
                        title="Move Up"
                      >
                        ▲
                      </button>
                      <button
                        className="section-action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSectionDown(sectionId);
                        }}
                        title="Move Down"
                      >
                        ▼
                      </button>
                      <button
                        className={`section-action-btn ${sectionData.visible ? "visible" : "hidden"}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSectionVisibility(sectionId);
                        }}
                        title={sectionData.visible ? "Hide Section" : "Show Section"}
                      >
                        {sectionData.visible ? "👁️" : "🚫"}
                      </button>
                    </div>
                  </div>
                  <div className="section-item-meta">
                    <span className="section-type">{sectionConfig.type}</span>
                    {sectionConfig.hasVideo && <span className="section-feature">🎥 Video</span>}
                    {sectionConfig.hasText && <span className="section-feature">📝 Text</span>}
                    {sectionConfig.hasLinks && <span className="section-feature">🔗 Links</span>}
                  </div>
                </div>
              );
            })}
          </div>

          <button className="add-section-btn">
            ➕ Add New Section
          </button>
        </div>

        {/* Center - Visual Preview */}
        <div className={`pb-canvas-area ${viewMode}`}>
          <div className="canvas-header">
            <h3>
              {currentPage.icon} {currentPage.name} - {viewMode.charAt(0).toUpperCase() + viewMode.slice(1)} Preview
            </h3>
            <div className="edit-mode-toggle">
              <button
                className={`mode-btn ${editMode === "visual" ? "active" : ""}`}
                onClick={() => setEditMode("visual")}
              >
                👁️ Visual
              </button>
              <button
                className={`mode-btn ${editMode === "code" ? "active" : ""}`}
                onClick={() => setEditMode("code")}
              >
                💻 Code
              </button>
            </div>
          </div>

          <div className="canvas-viewport">
            {editMode === "visual" ? (
              <div className="canvas-preview">
                {getSortedSections().map(([sectionId, sectionData]) => {
                  if (!sectionData.visible) return null;
                  
                  return (
                    <div
                      key={sectionId}
                      className={`preview-section ${selectedSection === sectionId ? "selected" : ""}`}
                      onClick={() => setSelectedSection(sectionId)}
                    >
                      <div className="preview-section-header">
                        <span className="preview-section-title">
                          {sectionData.title || sectionId}
                        </span>
                        {selectedSection === sectionId && (
                          <span className="editing-indicator">✏️ Editing</span>
                        )}
                      </div>
                      <div className="preview-section-content">
                        <div className="preview-placeholder">
                          <p>Section content preview will appear here</p>
                          <small>Click the properties panel to edit this section</small>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="code-editor">
                <pre className="code-preview">
                  {JSON.stringify(pageData[selectedPage], null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar - Properties Panel */}
        <div className="pb-sidebar-right">
          <div className="sidebar-header">
            <h3>⚙️ Properties</h3>
            <p className="sidebar-desc">
              {selectedSection ? "Edit selected section" : "Select a section to edit"}
            </p>
          </div>

          {selectedSection ? (
            <div className="properties-panel">
              {(() => {
                const sectionData = pageData[selectedPage]?.sections?.[selectedSection];
                const sectionConfig = currentPage.sections.find(s => s.id === selectedSection);
                if (!sectionData || !sectionConfig) return null;

                return (
                  <>
                    <div className="property-group">
                      <h4>General</h4>
                      <div className="property-field">
                        <label>Section Title</label>
                        <input
                          type="text"
                          className="vm-input"
                          value={sectionData.title || ""}
                          onChange={(e) => updateSectionField(selectedSection, "title", e.target.value)}
                          placeholder="Enter section title..."
                        />
                      </div>
                      <div className="property-field">
                        <label>Visibility</label>
                        <div className="toggle-switch">
                          <input
                            type="checkbox"
                            id={`visible-${selectedSection}`}
                            checked={sectionData.visible || false}
                            onChange={(e) => updateSectionField(selectedSection, "visible", e.target.checked)}
                          />
                          <label htmlFor={`visible-${selectedSection}`}>
                            {sectionData.visible ? "Visible" : "Hidden"}
                          </label>
                        </div>
                      </div>
                    </div>

                    {sectionConfig.hasText && (
                      <div className="property-group">
                        <h4>Text Content</h4>
                        <div className="property-field">
                          <label>Heading</label>
                          <input
                            type="text"
                            className="vm-input"
                            value={sectionData.content?.heading || ""}
                            onChange={(e) => updateSectionContent(selectedSection, "heading", e.target.value)}
                            placeholder="Enter heading..."
                          />
                        </div>
                        <div className="property-field">
                          <label>Description</label>
                          <textarea
                            className="vm-input"
                            rows="4"
                            value={sectionData.content?.description || ""}
                            onChange={(e) => updateSectionContent(selectedSection, "description", e.target.value)}
                            placeholder="Enter description..."
                          />
                        </div>
                      </div>
                    )}

                    {sectionConfig.hasVideo && (
                      <div className="property-group">
                        <h4>Video Content</h4>
                        <div className="property-field">
                          <label>Video URL</label>
                          <input
                            type="text"
                            className="vm-input"
                            value={sectionData.content?.videoUrl || ""}
                            onChange={(e) => updateSectionContent(selectedSection, "videoUrl", e.target.value)}
                            placeholder="https://..."
                          />
                        </div>
                        <div className="property-field">
                          <label>Video Title</label>
                          <input
                            type="text"
                            className="vm-input"
                            value={sectionData.content?.videoTitle || ""}
                            onChange={(e) => updateSectionContent(selectedSection, "videoTitle", e.target.value)}
                            placeholder="Enter video title..."
                          />
                        </div>
                        <div className="property-field">
                          <label>Thumbnail URL</label>
                          <input
                            type="text"
                            className="vm-input"
                            value={sectionData.content?.thumbnailUrl || ""}
                            onChange={(e) => updateSectionContent(selectedSection, "thumbnailUrl", e.target.value)}
                            placeholder="https://..."
                          />
                        </div>
                      </div>
                    )}

                    {sectionConfig.hasLinks && (
                      <div className="property-group">
                        <h4>Links</h4>
                        <div className="property-field">
                          <label>Link URL</label>
                          <input
                            type="text"
                            className="vm-input"
                            value={sectionData.content?.linkUrl || ""}
                            onChange={(e) => updateSectionContent(selectedSection, "linkUrl", e.target.value)}
                            placeholder="https://..."
                          />
                        </div>
                        <div className="property-field">
                          <label>Link Text</label>
                          <input
                            type="text"
                            className="vm-input"
                            value={sectionData.content?.linkText || ""}
                            onChange={(e) => updateSectionContent(selectedSection, "linkText", e.target.value)}
                            placeholder="Enter link text..."
                          />
                        </div>
                      </div>
                    )}

                    <div className="property-group">
                      <h4>Layout</h4>
                      <div className="property-field">
                        <label>Background Color</label>
                        <input
                          type="color"
                          className="vm-input"
                          value={sectionData.content?.backgroundColor || "#000000"}
                          onChange={(e) => updateSectionContent(selectedSection, "backgroundColor", e.target.value)}
                        />
                      </div>
                      <div className="property-field">
                        <label>Padding</label>
                        <input
                          type="text"
                          className="vm-input"
                          value={sectionData.content?.padding || ""}
                          onChange={(e) => updateSectionContent(selectedSection, "padding", e.target.value)}
                          placeholder="e.g., 2rem, 20px"
                        />
                      </div>
                    </div>

                    <div className="property-actions">
                      <button
                        className="btn-reset"
                        onClick={() => {
                          if (window.confirm("Reset this section to default?")) {
                            updateSectionField(selectedSection, "content", {});
                          }
                        }}
                      >
                        🔄 Reset Section
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          ) : (
            <div className="no-selection">
              <div className="no-selection-icon">👈</div>
              <p>Select a section from the left to edit its properties</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Access Bar */}
      <div className="pb-quick-access">
        <button className="quick-btn" onClick={() => window.location.reload()}>
          🔄 Refresh Preview
        </button>
        <button className="quick-btn" onClick={() => setSelectedSection(null)}>
          ✖️ Deselect
        </button>
        <button className="quick-btn" onClick={() => {
          if (window.confirm("Are you sure you want to reset all sections?")) {
            setPageData(prev => ({
              ...prev,
              [selectedPage]: initializePageData(currentPage)
            }));
          }
        }}>
          ⚠️ Reset Page
        </button>
      </div>
    </div>
  );
}
